import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {useLocation, useNavigate} from 'react-router';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {C_ORIGIN} from '@/constants/OriginConstants';
import {useAmplifyAuth, useCountryCode} from '@/hooks';
import {
  AuthTypesFlow,
  CAwsAmplifyAuthChallenges,
  SignInInput,
} from '@/services';
import {isDevelopment} from '@/utils/environment';
import logError from '@/utils/errors/logError';
import {generateUuid} from '@/utils/randomGenerator';

import {DEV_PASSWORD} from '../../constants/devDefaults';
import {useVerifyAuthProcess} from '../../hooks/useVerifyAuthProcess';

import {defaultToast, DEV_PHONE_NUMBER, errorMap} from './constants';
import {useTrackers} from './trackers';
import {SignInViewModelContextType} from './types';

const SignInViewModelContext = createContext<
  SignInViewModelContextType | undefined
>(undefined);

export function SignInViewModelProvider({children}: {children: ReactNode}) {
  const location = useLocation();
  const signInData = location.state || {};
  const {isCountryCodeVisible} = useCountryCode();

  const [phone, setPhone] = useState<string>(
    signInData.phone || (isDevelopment ? DEV_PHONE_NUMBER : undefined),
  );
  const [password, setPassword] = useState<string>(
    isDevelopment && !signInData.phone ? DEV_PASSWORD : undefined,
  );

  const {
    signIn,
    signOut,
    getCurrentSession,
    isLoading: isLoadingAmplifyAuth,
  } = useAmplifyAuth();

  const navigate = useNavigate();

  const {verifyAuthProcess} = useVerifyAuthProcess();

  const {trackViewSignIn, trackSelectSignInContinue} = useTrackers();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      logError(error, {
        fileName: 'SignIn ViewModelContext',
        functionName: 'handleSignOut',
      });
    }
  }, [signOut]);

  const handlePressSignIn = useCallback(async () => {
    await handleSignOut();

    const uuid = generateUuid();

    const request: SignInInput = {
      username: phone,
      password,
    };

    const metadata = {
      shared_key: uuid,
    };

    trackSelectSignInContinue();
    try {
      const result = await signIn(
        request,
        AuthTypesFlow.USER_SRP_AUTH,
        metadata,
      );

      switch (result?.nextStep.signInStep) {
        case CAwsAmplifyAuthChallenges.CONFIRM_SIGN_UP:
          navigate('/authentication/sign-up', {
            state: {
              phone,
              sharedKey: uuid,
            },
          });
          break;
        case CAwsAmplifyAuthChallenges.CONFIRM_SIGN_IN_WITH_EMAIL_CODE:
        case CAwsAmplifyAuthChallenges.CONFIRM_SIGN_IN_WITH_SMS_CODE:
          navigate('/authentication/verify', {
            state: {
              phone,
              sharedKey: uuid,
              password,
            },
          });
          break;
        case CAwsAmplifyAuthChallenges.DONE: {
          // Cognito can return DONE straight from `signIn` (e.g. a
          // remembered device / no MFA challenge), skipping the OTP screen
          // entirely. That screen is the only place that persists the
          // session token and identifies the user for analytics, so we
          // have to replicate that here or this session never gets
          // associated with the user's id (mixpanel.identify never runs).
          const sessionResult = await getCurrentSession(true);
          if (sessionResult) {
            verifyAuthProcess(sessionResult, {
              flow: C_ORIGIN.SIGNIN,
              phone,
            });
          }
          navigate('/home', {
            state: {
              phone,
              sharedKey: uuid,
            },
          });
          break;
        }
      }
    } catch (error) {
      logError(error, {
        fileName: 'SignIn ViewModelContext',
        functionName: 'handlePressSignIn',
        extraTags: {
          'user.phone': phone,
        },
      });
      const errName = error?.name || '';
      const {TITLE: summary, DETAILS: detail} =
        errorMap[errName] || defaultToast;
      showSnackbar({
        summary: summary,
        message: detail,
      });
    }
  }, [
    phone,
    password,
    trackSelectSignInContinue,
    handleSignOut,
    signIn,
    getCurrentSession,
    verifyAuthProcess,
    navigate,
  ]);

  const handlePressSignUp = useCallback(() => {
    navigate('/authentication/sign-up');
  }, [navigate]);

  const handlePressForgotPassword = useCallback(() => {
    navigate('/authentication/password/forgot');
  }, [navigate]);

  useEffect(() => {
    trackViewSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SignInViewModelContext.Provider
      value={{
        phone,
        setPhone,
        password,
        setPassword,
        isCountryCodeVisible,
        isLoadingAmplifyAuth,
        handlePressSignIn,
        handlePressSignUp,
        handlePressForgotPassword,
      }}>
      {children}
    </SignInViewModelContext.Provider>
  );
}

export function useSignInViewModelProvider() {
  const context = useContext(SignInViewModelContext);
  if (context === undefined) {
    throw new Error('SignIn ViewModelContext is undefined');
  }
  return context;
}
